import { Component, inject, signal, WritableSignal, computed, Inject, PLATFORM_ID, Signal, effect, afterNextRender, viewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStep, MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { AddressService, NavegoAddressResponse } from '../../shared/address.service';
import { DeliveryAddress, PersonalInformation, SignUpProducts } from './signup.types';
import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import { AccountService, PaymentMethod } from '../../shared/account.service';
import { Router, RouterLink } from '@angular/router';
import { LocalStorageService } from '../../shared/local-storage.service';
import { SignalsStoreService } from '../../shared/signals-store.service';
import { Session } from '../../shared/types/session.type';
import { LOCALSTORAGE_KEYS } from '../../shared/constants/databases';
import { ArrayOptionsItem, NewAccountPayload, AccountSettings, DeliveryOptionsResponseItem, PaymentDataPayload } from '../../shared/types/account.types';
import { CheckAddressSignupComponent } from './check-address-signup/check-address-signup.component';
import { PaymentMethodSignupComponent } from './payment-method-signup/payment-method-signup.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { finalize, tap } from 'rxjs';
import { OnlyNumberDirective } from '../../shared/directives/only-number.directive';
import { AuthorizeService } from '../../shared/purchase/authorize/authorize.service';
import { ANetPayload, ANetResponse } from '../../shared/purchase/authorize/authorize.types';
import { ApiResponse } from '../../shared/common/types';
import { ModalContentService } from '../../shared/modal-content/modal-content.service';
import { ModalContentTypes } from '../../shared/constants/modal-content-types';
import { HTTP_STATUS_CODES } from '../../shared/constants/errors';
import { FooterComponent } from '../../shared/footer/footer.component';
import { FaqsComponent } from './faqs/faqs.component';
import { WelcomeComponent } from './welcome/welcome.component';
import { ProductsService } from '../../product/products.service';
import { HeaderService } from '../../shared/header/header.service';
import { DeliveriesService } from '../../settings/account/deliveries/deliveries.service';
import { scrollToTop } from '../../shared/common/utils';
import { Product } from '../../product/product.types';
import { MarketingReferral } from '../../shared/types/common.types';
import { checkSignupCouponCodes } from '../../shared/common/singup.utils';
import { PromotionsService } from '../../shared/promotions.service';
import { NotificationService } from '../../shared/notification/notification.service';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { formatDateToReadableString } from '../../shared/utils/formatting';
import { DataLayerService } from '../../shared/data-layer/data-layer.service';
import { KlaviyoService } from '../../shared/klaviyo/klaviyo.service';
import { KLAVIYOEVENTS } from '../../shared/klaviyo/events';

@Component({
  selector: 'app-signup',
  imports: [
    CommonModule,
    MatButtonModule,
    MatStepperModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatProgressBarModule,
    RouterLink,
    CheckAddressSignupComponent,
    PaymentMethodSignupComponent,
    MatDatepickerModule,
    OnlyNumberDirective,
    FooterComponent,
    FaqsComponent,
    WelcomeComponent,
    FaqsComponent,
    FooterComponent,
    NgbTooltip
  ],
  // providers: [
  //   { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
  //   { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS.MONTH_DAY_YEAR },
  // ],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss',
  host: { ngSkipHydration: 'true' }
})

export class SignupComponent {
  private localStorageService = inject(LocalStorageService);
  private signalsStoreService = inject(SignalsStoreService);
  private addressService = inject(AddressService);
  private klaviyoService = inject(KlaviyoService);
  accountService = inject(AccountService);
  #authorizeService = inject(AuthorizeService);
  #modalContentService = inject(ModalContentService);
  #productsService = inject(ProductsService);
  #router = inject(Router);
  #headerService = inject(HeaderService);
  #deliveriesService = inject(DeliveriesService);
  #location = inject(Location);
  #promotionsService = inject(PromotionsService);
  #notificationService = inject(NotificationService);
  #dataLayerService = inject(DataLayerService);

  matStepper = viewChild.required<MatStepper>('stepper');
  firstStep = viewChild.required<MatStep>('firstStep');
  thirdStep = viewChild.required<MatStep>('thirdStep');
  fourthStep = viewChild.required<MatStep>('fourthStep');

  isLinear = true;

  //#region Check delivery address:
  deliveryAddress: WritableSignal<Partial<DeliveryAddress>> = signal({
    additionalAddress: '',
    address: '',
    city: '',
    deliveryNotes: '',
    stateCode: '',
    zipCode: '',
  });

  isGoogleAddressFilled = signal(false);
  hasDeliveryAddressCoverage: WritableSignal<boolean> = signal(false);
  isLoadingContent: WritableSignal<boolean> = this.addressService.isLoading;
  //#endregion

  personalInformation: WritableSignal<PersonalInformation | null> = signal(null)

  referralSource: any[] = [];
  referralSourceOpened: boolean = false;
  deliveryOptions: DeliveryOptionsResponseItem[] = [];
  timeSet: Signal<ArrayOptionsItem[]> = computed(() => this.setUpTimeSet());

  //#region Payment method
  paymentMethod: WritableSignal<PaymentMethod> = signal({
    cardCode: '',
    firstName: '',
    lastName: '',
    cardNumber: '',
    email: '',
    expirationDate: '',
  });

  isValidFormPaymentMethod = signal(false);
  //#endregion

  isCheckedAddAnotherContact: WritableSignal<boolean> = signal(false);
  isCheckedCouponCode: boolean = false;


  selectedDeliveryDay: WritableSignal<DeliveryOptionsResponseItem | null> = signal(null);
  selectedDeliveryTimeWindow: any = signal(null);
  selectedReferralSource: any = signal(null);
  selectedReferralSourceInfo: Signal<{ id: any, name: string, hasAdditionalInfo: boolean } | null> = computed(() => {
    const selectedId = this.selectedReferralSource();
    const info = this.referralSource.find(r => r.id === selectedId);
    return info || null
  })
  selectedMonth: any = null;
  selectedYear: any = null;
  isPasswordValid1 = computed(() => !!(this.passwordConditions.every(condition => condition.valid())));
  isValidFormDeliveryAddress = computed(() => this.hasDeliveryAddressCoverage() && !!this.selectedDeliveryDay() && !!this.selectedDeliveryTimeWindow());
  isValidFormPersonalInformation = computed(() => this.validatePersonalInfoForm());
  personalInformationValidations = new Map<string, WritableSignal<{ valid: boolean, error: string[], validations: string[] }>>();
  shouldDisableCreateClientButton = signal(false);

  hidePassword = true;

  isMinLengthValid = computed(() => (this.personalInformation()?.password ?? '').length >= 8)
  hasUpperCase = computed(() => /[A-Z]/.test(this.personalInformation()?.password || ''))
  hasLowerCase = computed(() => /[a-z]/.test(this.personalInformation()?.password || ''))
  hasSpecialChar = computed(() => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(this.personalInformation()?.password || ''))
  hasNumber = computed(() => /\d/.test(this.personalInformation()?.password || ''))
  hasNotEmptySpaces = computed(() => !((this.personalInformation()?.password || '').includes(' ')))
  passwordConditions = [
    { label: 'At least 8 Characters', valid: () => this.isMinLengthValid() },
    { label: 'At least 1 Uppercase (A-Z)', valid: () => this.hasUpperCase() },
    { label: 'At least 1 Lowercase (a-z)', valid: () => this.hasLowerCase() },
    { label: 'At least 1 Symbol', valid: () => this.hasSpecialChar() },
    { label: 'At least 1 Number (0-9)', valid: () => this.hasNumber() },
    { label: 'Do not include spaces', valid: () => this.hasNotEmptySpaces() },
  ];

  shouldSetUpSettings: boolean = true;

  checkboxValues: boolean[] = [true, true, true];

  logoVersionNumber = computed(() => this.signalsStoreService.logoVersionNumber());

  isCheckContinue = signal(false);
  hasGlobalMessages = computed(() => !!this.signalsStoreService.globalMessages().length);

  signupPromotionInfo = computed(() => this.signalsStoreService.signupPromotionInfo());
  
  signupProducts = signal<Map<number | string, SignUpProducts>>(new Map());

  isLoadedMarketingValidations = computed(() => this.signalsStoreService.isLoadedMarketingValidation());

  isTryingToApplyCoupon = signal(false);

  signupPromotionInfoFromLS = computed(() => this.signalsStoreService.signupPromotionInfoLS());

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    if (!isPlatformBrowser(this.platformId)) return;
    effect(() => {
      if (this.hasDeliveryAddressCoverage())
        this.getDeliveryOptions();
    });

    afterNextRender(() => {
      const sessionStored: Session = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) as any
      const hasPaymentMethod = !!(sessionStored)?.settings?.hasPaymentMethod;
      const stepToLoad = this.signalsStoreService.hasSession() && !hasPaymentMethod ? 2 : 0
      // Load first step or payment method step if hasSession
      if (stepToLoad > 0) {
        for (let index = 0; index < stepToLoad; index++) {
          this.matStepper().steps.toArray()[index].completed = true;
        }
      }

      this.matStepper().selectedIndex = stepToLoad;

      this.setUpPersonalInfoValidations();
    })
  }

  updateCheckboxValue(checked: boolean, index: number) {
    this.checkboxValues[index] = checked;
  }

  updatePersonalInformation(newValue: any, formValidationKey: string, isContactPerson?: boolean) {
    if (isContactPerson) {
      this.personalInformation.update((value: any) => ({ ...value, contactPerson: { ...value.contactPerson, ...newValue } }));
    } else {
      this.personalInformation.update(value => ({ ...value, ...newValue }));
    }

    // Validate form fields:
    const key = Object.keys(newValue)[0];
    const value = newValue[key];
    this.validatePersonalInfoField(formValidationKey, value);
  }

  private validatePersonalInfoForm() {
    const formFilled = !!(
      this.personalInformation()?.firstName &&
      this.personalInformation()?.lastName &&
      this.personalInformation()?.phone &&
      this.personalInformation()?.email &&
      this.isPasswordValid1() &&
      this.selectedReferralSourceInfo() &&
      (
        !this.selectedReferralSourceInfo()?.hasAdditionalInfo ||
        (
          this.selectedReferralSourceInfo()?.hasAdditionalInfo &&
          this.personalInformation()?.additionalInfo
        )
      ) &&
      (
        !this.isCheckedAddAnotherContact() ||
        (
          this.isCheckedAddAnotherContact() &&
          this.personalInformation()?.contactPerson.firstName &&
          this.personalInformation()?.contactPerson.lastName &&
          this.personalInformation()?.contactPerson.email &&
          this.personalInformation()?.contactPerson.phone
        )
      )
    );

    if (!formFilled) return false;

    let valid = true;
    const keys: string[] = Array.from(this.personalInformationValidations.keys());
    for (const key of keys) {
      if (!this.personalInformationValidations.get(key)?.()?.valid) {
        valid = false;
        break;
      }
    }

    return valid;
  }

  showValidationError() {
    const keys: string[] = Array.from(this.personalInformationValidations.keys());
    const data: any = this.personalInformation();

    for (const key of keys) {

      let value = '';

      if (key === 'contactFirstName')
        value = data.contactPerson.firstName;
      else if (key === 'contactLastName')
        value = data.contactPerson.lastName;
      else if (key === 'contactEmail')
        value = data.contactPerson.email;
      else if (key === 'contactPhone')
        value = data.contactPerson.phone;
      else
        value = data[key];

      this.validatePersonalInfoField(key, value?.trim() ?? '');
    }
  }

  createNewClient() {

    // Validate the form
    if (!this.validatePersonalInfoForm()) {
      return this.showValidationError();
    }

    if (this.shouldDisableCreateClientButton()) return;
    this.shouldDisableCreateClientButton.set(true);
    const payload = this.setUpNewClientPayload();
    if (!payload) return;
    this.accountService.create(payload).pipe(
      finalize(() => this.shouldDisableCreateClientButton.set(false))
    ).subscribe({
      next: (res: ApiResponse<Session>) => {
        const { data, status } = res;
        if (status === HTTP_STATUS_CODES.CONFLICT)
          return this.#showModalEmailAlreadyInUse(payload.accountInfo.email);
        if (data?.isTimeSlotCapacityFull) {
          this.selectedDeliveryDay.set(null);
          this.selectedDeliveryTimeWindow.set(null);
          this.getDeliveryOptions();
          this.firstStep().editable = true;
          this.matStepper().previous();
        } else if (data) {
          // Lets clean the marketing referral info from local storage:
          this.localStorageService.remove(LOCALSTORAGE_KEYS.MARKETING_REFERRAL);
          this.matStepper().next();
          this.shouldDisableCreateClientButton.set(false);
        }

        setTimeout(() => {
          scrollToTop('scroll-to-top-ref');
        }, 50);
      }
    });
  }

  #showModalEmailAlreadyInUse(email: string) {
    this.#modalContentService.openModal(ModalContentTypes.CONFIRMATION, {
      title: 'Email already in use',
      textContent: `Hey! Looks like you already have an account with us. Would you like to login with <b><i>${email}</i></b>?`,
      cancelButtonText: 'Try another email',
      confirmButtonText: 'Click here to login',
    }).closed
      .subscribe((res: { confirm: boolean } | null) => {
        if (!res?.confirm) return this.#clearEmailField();
        this.localStorageService.set(LOCALSTORAGE_KEYS.PRELOADED_EMAIL, email);
        this.#router.navigate(['/login']);
      })
  }

  #clearEmailField() {
    this.personalInformation.update((value: any) => ({ ...value, email: '' }));
  }

  private setUpNewClientPayload(): NewAccountPayload | null {
    try {
      const { firstName, lastName, phone, email, password, additionalInfo, contactPerson: contact } = this.personalInformation() || {};
      const { additionalAddress: additional, zipCode: zip, deliveryNotes: noteDriver } = this.deliveryAddress()
      const { street, zoneId, zoneName, city, state: stateCode, latitude, longitude } = this.addressService.navegoAddress() as NavegoAddressResponse
      return {
        accountInfo: {
          email,
          firstName,
          lastName,
          noteDriver,
          password,
          phone
        },
        address: {
          additional,
          city,
          latitude,
          longitude,
          stateCode,
          street,
          zip,
          noteDriver,
          zoneId
        },
        settings: {
          receiveNewsletter: this.checkboxValues[1],
          receiveText: this.checkboxValues[0],
          receiveTextDelivery: this.checkboxValues[2],
          referral: {
            additionalInfo: this.selectedReferralSourceInfo()?.hasAdditionalInfo ? additionalInfo : null,
            source: this.selectedReferralSource()
          },
          delivery: {
            dayId: this.selectedDeliveryDay()?.id,
            timeWindowId: this.selectedDeliveryTimeWindow() === -1 ? null : this.selectedDeliveryTimeWindow()
          }
        },
        contact: this.isCheckedAddAnotherContact() ? contact : { firstName: null, lastName: null, email: null, phone: null }
      } as NewAccountPayload
    } catch (error) {
      console.log('error >>>', error);
      return null;
    }
  }

  sendDataToANet() {
    const value = this.paymentMethod();
    const [month, year] = value.expirationDate.split('/');
    const ANetData: ANetPayload = {
      cardCode: value.cardCode,
      cardNumber: value.cardNumber,
      year,
      month
    };
    this.#authorizeService.sendPaymentDataToAnet(ANetData, (response: ANetResponse) => this.addPaymentMethod(response))
  }

  addPaymentMethod(opaqueData: ANetResponse) {
    const payload = this.setUpPaymentMethodPayload(opaqueData);
    this.accountService
      .addPaymentMethod(payload)
      .pipe(
        tap(res => {
          this.localStorageService.set(LOCALSTORAGE_KEYS.NEW_ACCOUNT, true);
          // Register klaviyo event:
          this.registerKlaviyoNewSignupEvent();
          // Track data layer signup event:
          this.#dataLayerService.trackSignUpEvent();
        }),
        tap(res => res?.data && this.continue())
      )
      .subscribe();
  }

  private registerKlaviyoNewSignupEvent() {
    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION)
    if (!session) return;
    if (session && session.deliveryInfo?.deliveryDay) {
      // Sign Up Date Config:
      const currentDate = new Date();
      const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
      const currentDay = (currentDate.getDate()).toString().padStart(2, '0');
      const signupDate = `${currentDate.getFullYear()}-${currentMonth}-${currentDay}`;
      const formattedSignupDate = formatDateToReadableString(signupDate).mmddyyyyFormat.replace(/\//g, "-");
      // ***********************
      this.klaviyoService.trackEvent(KLAVIYOEVENTS.NewSignUp, {
        'Sign Up Date': formattedSignupDate,
        'Delivery Date': session.deliveryInfo?.deliveryDay,
        'Cut-Off Day and Time': `${session.deliveryInfo?.cutoffDay} ${session.deliveryInfo?.cutoffTime}`
      })
    };
  }

  private setUpPaymentMethodPayload(opaqueData: any): PaymentDataPayload {
    const values = this.paymentMethod();
    const cardNumber = values.cardNumber.replaceAll(' ', '');
    const cardLastNumbers = cardNumber.substring(cardNumber.length - 4, cardNumber.length);
    return {
      billingAddress: {
        additional: values.additionalAddress,
        city: values.city,
        stateCode: values.state,
        street: values.address,
        zip: values.zipCode
      },
      card: {
        firstName: values.firstName || '',
        lastName: values.lastName || '',
        expirationDate: values.expirationDate?.split('/').reverse().join('-') || '',
        number: cardLastNumbers,
      },
      opaqueData
    };
  }

  validateDeliveryAddress() {
    this.isCheckContinue.set(true);

    if (!this.firstStep().completed)
      return;

    this.continue();
  }

  continue() {

    const currentIndex = this.matStepper().selectedIndex;

    switch (currentIndex) {
      case 0:
        this.firstStep().completed = true;
        break;
      case 2:
        this.#setUpStep4();
        break;
    }

    this.matStepper().next();

    if (this.matStepper().selectedIndex === 1) {
      this.firstStep().editable = false;
      if (this.shouldSetUpSettings) {
        this.getAccountSettings();
        this.shouldSetUpSettings = false;
      }
    }

    scrollToTop('scroll-to-top-ref');
  }

  #setUpStep4() {
    this.thirdStep().completed = true;
    this.#getSignUpProducts();
    this.#headerService.getFrequencies();
    this.#deliveriesService.getDeliveryZoneInfo().subscribe();
    this.signalsStoreService.getAndSyncCart();
  }

  private getAccountSettings() {
    this.accountService.getAccountSettings().subscribe({
      next: (accountSettings: AccountSettings | null) => {
        if (accountSettings) {
          this.setUpAccountSettings(accountSettings);
        }
      }
    });
  }

  private getDeliveryOptions() {
    this.accountService.getDeliveryOptions(this.addressService.navegoAddress()?.zoneId).subscribe({ next: (options: DeliveryOptionsResponseItem[]) => this.deliveryOptions = options });
  }

  private setUpAccountSettings(accountSettings: AccountSettings) {
    this.personalInformation.set({
      contactPerson: {
        email: null,
        firstName: null,
        lastName: null,
        phone: null,
      },
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      phone: '',
      referralSource: '',
      additionalInfo: ''
    });

    this.referralSource = accountSettings.references;
  }

  setSelectedDeliveryDay(event: MatSelectChange) {
    const option = this.deliveryOptions.find(o => o.id === event.value);
    if (option) {
      this.selectedDeliveryDay.set(option);
      this.selectedDeliveryTimeWindow.set(null)
    }
  }

  private setUpTimeSet() {
    const selectedDay = this.selectedDeliveryDay();
    return (selectedDay && selectedDay.timeSet && selectedDay.timeSet.length) ? selectedDay.timeSet : [];
  }

  private setUpPersonalInfoValidations() {
    const defaultValue = { valid: true, error: [], validations: ['required'] };
    this.personalInformationValidations.set('firstName', signal(defaultValue));
    this.personalInformationValidations.set('lastName', signal(defaultValue));
    this.personalInformationValidations.set('phone', signal({ ...defaultValue, validations: [...defaultValue.validations, 'phone'] }));
    this.personalInformationValidations.set('email', signal({ ...defaultValue, validations: [...defaultValue.validations, 'email'] }));
    this.personalInformationValidations.set('password', signal({ ...defaultValue, validations: [...defaultValue.validations, 'password'] }));
    this.personalInformationValidations.set('referralSource', signal(defaultValue));
    this.personalInformationValidations.set('additionalInfo', signal(defaultValue));

    // { ...defaultValue, validations: [...defaultValue.validations, 'email'] }
    // Default without validations
    this.personalInformationValidations.set('contactFirstName', signal({ ...defaultValue, validations: [] }));
    this.personalInformationValidations.set('contactLastName', signal({ ...defaultValue, validations: [] }));
    this.personalInformationValidations.set('contactEmail', signal({ ...defaultValue, validations: [] }));
    this.personalInformationValidations.set('contactPhone', signal({ ...defaultValue, validations: [] }));
  }

  validatePersonalInfoField(key: string, value: string | any) {

    if (typeof value !== 'string')
      value = value.target.value;

    const entry = this.personalInformationValidations.get(key);
    if (!entry)
      return;

    const errors = [];
    const data = entry();
    value = value.trim();
    for (const validation of entry().validations) {
      switch (validation) {
        case 'required':
          if (!value.trim())
            errors.push('This field is required');
          break;
        case 'phone':
          if (value && (!(/^\d+$/.test(value)) || value.trim().length !== 10))
            errors.push('Enter a valid phone number');
          break;
        case 'email':
          if (value && !(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{1,6}$/.test(value)))
            errors.push('Enter a valid email');
          break;
        case 'password':
          if (!this.isPasswordValid1())
            errors.push('Enter a valid password');
          break;
        default:
          break;
      }
    }

    this.personalInformationValidations.set(key, signal({
      valid: !errors.length,
      error: errors,
      validations: data.validations
    }));

  }

  onSelectReferralSource() {
    this.referralSourceOpened = true;
  }

  anotherAccountSlideChanged(value: boolean) {

    this.personalInformation.update((value: any) => ({
      ...value,
      contactPerson: {
        firstName: null,
        lastName: null,
        email: null,
        phone: null
      }
    }));

    if (value) {
      const defaultValue = { valid: true, error: [], validations: ['required'] };
      this.personalInformationValidations.set('contactFirstName', signal(defaultValue));
      this.personalInformationValidations.set('contactLastName', signal(defaultValue));
      this.personalInformationValidations.set('contactEmail', signal({ ...defaultValue, validations: [...defaultValue.validations, 'email'] }));
      this.personalInformationValidations.set('contactPhone', signal({ ...defaultValue, validations: [...defaultValue.validations, 'phone'] }));
    } else {
      const defaultValue = { valid: true, error: [], validations: [] };
      this.personalInformationValidations.set('contactFirstName', signal(defaultValue));
      this.personalInformationValidations.set('contactLastName', signal(defaultValue));
      this.personalInformationValidations.set('contactEmail', signal(defaultValue));
      this.personalInformationValidations.set('contactPhone', signal(defaultValue));
    }
  }

  #getSignUpProducts() {

    this.signupProducts.set(new Map());

    this.#productsService
      .getSignupProducts()
      .pipe(
        tap(res => this.#setUpSignUpProducts(res?.data ?? []))
      )
      .subscribe();
  }

  #setUpSignUpProducts(products: Product[]) {

    products = products.filter(x => x.isSubscription);

    if (!products.length)
      return;

    const resp = new Map<number | string, SignUpProducts>();
    for (const product of products) {

      const { id, name } = product.category;

      if (!resp.has(product.category.id)) {
        resp.set(id, { category: name, products: [product] });
        continue;
      }

      const data = resp.get(id);
      if (data) {
        data.products.push(product);
        resp.set(id, data);
      }
    }

    this.signupProducts.set(resp);
  }

  applySignupCoupon(code: string) {

    if (!code) return this.#notificationService.show({ text: 'You should provide a coupon code.', type: 'error' });

    this.isTryingToApplyCoupon.set(true);

    const marketingReferral: MarketingReferral = {
      utmCampaign: code
    };

    checkSignupCouponCodes({
      marketingReferral,
      signalsStoreService: this.signalsStoreService,
      localStorageService: this.localStorageService,
      promotionsService: this.#promotionsService,
      showDefaultMessage: true
    }).pipe(
      tap(res => {
        if (res.name) {
          const currentPath = this.#location.path().split('?')[0];
          this.#location.replaceState(currentPath);
        }
      })
    ).subscribe();

  }

  clearSignupPromotionInfo() {
    this.signalsStoreService.setSignupPromotionInfo(null);
  }

  restoreSignupInfo() {
    const signupPromotionInfo = this.localStorageService.get(LOCALSTORAGE_KEYS.PROMOTION_INFO);
    if (!signupPromotionInfo) return;
    this.signalsStoreService.setSignupPromotionInfo(signupPromotionInfo);
  }
}
